/**
 * CommonIcon Component - Icon Wrapper for Lucide React Icons
 * 
 * Provides a unified interface for rendering icons from the lucide-react library
 * Handles icon name resolution, fallback for missing icons, and prop forwarding
 * 
 * Features:
 * - Dynamic icon name resolution from lucide-react
 * - Customizable size and color properties
 * - Fallback icon (AlertCircle) for invalid icon names
 * - Props forwarding for additional icon customization
 * - Type-safe icon selection
 * 
 * Available Icons:
 * All icons from lucide-react library are available (ChevronLeft, ChevronRight, X, Ban, etc.)
 * See: https://lucide.dev/
 * 
 * Dependencies:
 * - React: UI library
 * - lucide-react: Icon library providing SVG icons
 * 
 * @component
 * @param {Object} props - Component props
 * @param {string} props.iconName - Name of the icon to display (must match lucide-react export)
 * @param {number|string} [props.size=24] - Icon size in pixels or CSS string
 * @param {string} [props.color="black"] - Icon color (CSS color value)
 * @param {Object} [props...] - Additional props forwarded to the icon component
 * 
 * @returns {JSX.Element|null} Rendered icon component or fallback AlertCircle if icon not found
 * 
 * @example
 * // Basic usage with default size and color
 * <CommonIcon iconName="Home" />
 * 
 * @example
 * // Custom size and color
 * <CommonIcon iconName="ChevronRight" size={32} color="#0d6efd" />
 * 
 * @example
 * // With additional props
 * <CommonIcon iconName="X" size={20} color="white" strokeWidth={3} />
 * 
 * @example
 * // In a button
 * <button>
 *   <CommonIcon iconName="Settings" size={18} color="currentColor" />
 * </button>
 */

// CommonIcon.js
import React from 'react';
import * as Icons from 'lucide-react';

/**
 * CommonIcon Component
 * Renders lucide-react icons with customizable properties
 * 
 * @param {Object} props
 * @param {string} props.iconName - Icon identifier from lucide-react
 * @param {number|string} [props.size=24] - Icon dimensions
 * @param {string} [props.color="black"] - Icon color
 * @param {...Object} props - Additional props passed to icon component
 * @returns {JSX.Element} Icon element or fallback icon
 */
const CommonIcon = ({ iconName, size = 24, color = "black", ...props }) => {
  // Dynamically retrieve the icon component from lucide-react library
  // Icons are named exports: Icons.Home, Icons.Settings, etc.
  const IconComponent = Icons[iconName];
  
  // Fallback handling: Return AlertCircle icon in red if requested icon not found
  // This prevents errors and provides visual feedback for missing icons
  if (!IconComponent) {
    return <Icons.AlertCircle size={size} color="red" {...props} />; // Fallback icon for invalid names
  }
  
  // Render the requested icon with custom size, color, and additional props
  return <IconComponent size={size} color={color} {...props} />;
};

export default CommonIcon;